import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// Import Shared Components
import { ThemeProvider, ErrorComponent } from 'shared-components';
// Import Shared Constants
import { PRODUCTION } from 'shared-components/src/constants/nodeEnv';
// Import Shared Hooks
import useWhiteLabeling from 'shared-components/src/hooks/useWhiteLabeling';
// Import Shared Utils
import { initializeGlobalErrorHandler } from 'shared-components/src/utils/errors';
// Import Translations
import TRANSLATIONS from './i18n/translations';
// Import Root Component
import Root from './routes/Root';
// Import Utils
import { resolveInitialLocale } from './utils/locale';
// Error Logging
import { errorLogger } from './utils/errors';

// Attach Error Logger to Window to Catch Global Errors
if (process.env.NODE_ENV === PRODUCTION) {
  initializeGlobalErrorHandler(errorLogger);
}

function App() {
  const [locale, setLocale] = useState('en');

  useEffect(() => {
    // The CAMS wrapper sets the language via ?lang= (e.g. /cams?lang=fr) and carries it forward
    // on the links it follows, so ?lang= is honored on any page it lands on -- including the
    // cross-domain hop to payaccount.io, where sessionStorage does not carry. With no ?lang=,
    // every page keeps its original behavior: default en, honor a stored fr.
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    const initialLocale = resolveInitialLocale({
      urlLang,
      stored: sessionStorage.getItem('locale'),
    });
    // Persist an incoming ?lang= so the choice survives same-domain navigation and reloads.
    if (urlLang) {
      sessionStorage.setItem('locale', initialLocale);
    }
    if (initialLocale === 'fr') {
      setLocale('fr');
    }
  }, []);

  // Retreive white labeling for app
  const { loading, theme, localization, callCenterBanner, e6MigrationBanner,limitUpdateBanner, error } = useWhiteLabeling();

  if (loading) return <div />;

  if (error) return <ErrorComponent />;

  return (
    <HelmetProvider>
      {/* No key={locale} here: keying the provider remounts the whole tree on every language
          switch, which throws away in-progress form state -- a multi-step flow would restart
          from the beginning. react-intl re-renders on a locale/messages change by itself. */}
      <IntlProvider
        locale={locale}
        defaultLocale="en"
        messages={TRANSLATIONS[locale]}
      >
        <BrowserRouter>
          <ThemeProvider theme={theme}>
            <Root
              setLocale={setLocale}
              locale={locale}
              localization={localization}
              callCenterBanner={callCenterBanner}
              e6MigrationBanner={e6MigrationBanner}
              limitUpdateBanner={limitUpdateBanner}
            />
          </ThemeProvider>
        </BrowserRouter>
      </IntlProvider>
    </HelmetProvider>
  );
}

export default App;
